import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SupplierPageLayout from '../components/SupplierPageLayout';
import SupplierStatusBadge from '../components/SupplierStatusBadge';
import {
  PoProposalLine,
  dispatchSupplierOrder,
  formatPoDate,
  formatPoStatus,
  getSupplierOrder,
  poDateToInput,
  todayInput,
  proposeSupplierOrder,
} from '../services/supplierApi';

interface PoItem {
  id: number;
  ingredientName: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
  supplierProposedQty?: number;
  supplierProposedPrice?: number;
}

interface PoDetail {
  id: number;
  restroId: number;
  poNumber: string;
  status: string;
  supplierName?: string;
  expectedDate?: string | number;
  supplierProposedDate?: string | number;
  grandTotal?: number;
  items?: PoItem[];
}

const inr = (v?: number) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const changed = (a?: number, b?: number) => b != null && Math.abs(Number(a ?? 0) - Number(b)) > 0.0001;

const ProposedValue: React.FC<{ original?: number; proposed?: number; format: (v?: number) => string }> = ({
  original,
  proposed,
  format,
}) => {
  if (!changed(original, proposed)) {
    return <span>{format(original)}</span>;
  }
  const up = Number(proposed) > Number(original ?? 0);
  return (
    <span>
      <span className="sp-muted" style={{ textDecoration: 'line-through', marginRight: 6 }}>
        {format(original)}
      </span>
      <strong style={{ color: up ? '#B42318' : '#067647' }}>{format(proposed)}</strong>
    </span>
  );
};

const PODetail: React.FC = () => {
  const { restroId, id } = useParams();
  const poId = Number(id);
  const restaurantId = Number(restroId);
  const [po, setPo] = useState<PoDetail | null>(null);
  const [lines, setLines] = useState<PoProposalLine[]>([]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    getSupplierOrder(restaurantId, poId)
      .then((data) => {
        const loaded = data.po as PoDetail;
        setPo(loaded);
        const proposalLines = (loaded.items || []).map((item) => ({
          poItemId: item.id,
          proposedQty: item.supplierProposedQty ?? item.quantity,
          proposedPrice: item.supplierProposedPrice ?? item.unitPrice,
        }));
        setLines(proposalLines);
        const initialDate = poDateToInput(loaded.supplierProposedDate ?? loaded.expectedDate);
        setDeliveryDate(initialDate && initialDate >= todayInput() ? initialDate : '');
      })
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    if (!Number.isFinite(poId) || !Number.isFinite(restaurantId)) {
      setError('Invalid order');
      return;
    }
    load();
  }, [poId, restaurantId]);

  const updateLine = (poItemId: number, field: 'proposedQty' | 'proposedPrice', value: string) => {
    const num = value === '' ? undefined : Number(value);
    setLines((prev) => prev.map((l) => (l.poItemId === poItemId ? { ...l, [field]: num } : l)));
  };

  const handlePropose = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await proposeSupplierOrder(restaurantId, poId, { lines, deliveryDate: deliveryDate || undefined });
      setMessage('Proposal sent to restaurant for approval.');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDispatch = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await dispatchSupplierOrder(restaurantId, poId);
      setMessage('Order marked as dispatched.');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!po) {
    return (
      <SupplierPageLayout
        wide
        title="Purchase Order"
        icon="fa-file-text-o"
        breadcrumbs={[
          { label: 'Dashboard', to: '/supplier/dashboard' },
          { label: 'Orders', to: '/supplier/orders' },
          { label: 'Loading…' },
        ]}
      >
        {error ? <div className="sp-alert sp-alert--error">{error}</div> : <div className="sp-loading">Loading order…</div>}
      </SupplierPageLayout>
    );
  }

  const canPropose = po.status === 'PENDING_SUPPLIER';
  const canDispatch = po.status === 'APPROVED';
  const showProposal =
    po.status === 'SUPPLIER_PROPOSED' &&
    (po.items || []).some((i) => i.supplierProposedQty != null || i.supplierProposedPrice != null);
  const proposedTotal = (po.items || []).reduce(
    (sum, i) => sum + Number(i.supplierProposedQty ?? i.quantity ?? 0) * Number(i.supplierProposedPrice ?? i.unitPrice ?? 0),
    0,
  );
  const proposedDate = formatPoDate(po.supplierProposedDate);
  const requestedDate = formatPoDate(po.expectedDate);
  const dateMoved =
    po.supplierProposedDate != null &&
    po.expectedDate != null &&
    poDateToInput(po.supplierProposedDate) !== poDateToInput(po.expectedDate);

  return (
    <SupplierPageLayout
      wide
      title={`PO ${po.poNumber}`}
      subtitle={formatPoStatus(po.status)}
      icon="fa-file-text-o"
      breadcrumbs={[
        { label: 'Dashboard', to: '/supplier/dashboard' },
        { label: 'Orders', to: '/supplier/orders' },
        { label: po.poNumber },
      ]}
    >
      <div className="sp-actions" style={{ marginTop: 0, marginBottom: 12 }}>
        <Link to="/supplier/orders" className="sp-link">
          <i className="fa fa-arrow-left" aria-hidden="true" /> Back to orders
        </Link>
        <SupplierStatusBadge status={po.status} />
      </div>

      {error && <div className="sp-alert sp-alert--error">{error}</div>}
      {message && <div className="sp-alert sp-alert--success">{message}</div>}

      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th>Ordered qty</th>
              <th>Unit price</th>
              {canPropose && <th>Your qty</th>}
              {canPropose && <th>Your price</th>}
              {showProposal && <th>Your qty</th>}
              {showProposal && <th>Your price</th>}
              {showProposal && <th>Line total</th>}
            </tr>
          </thead>
          <tbody>
            {(po.items || []).map((item) => {
              const line = lines.find((l) => l.poItemId === item.id);
              return (
                <tr key={item.id}>
                  <td>{item.ingredientName}</td>
                  <td>{item.quantity} {item.unit || ''}</td>
                  <td>
                    {inr(item.unitPrice)}
                    {item.unit ? ` / ${item.unit}` : ''}
                  </td>
                  {showProposal && (
                    <>
                      <td>
                        <ProposedValue
                          original={item.quantity}
                          proposed={item.supplierProposedQty}
                          format={(v) => `${Number(v ?? 0)} ${item.unit || ''}`}
                        />
                      </td>
                      <td>
                        <ProposedValue original={item.unitPrice} proposed={item.supplierProposedPrice} format={inr} />
                      </td>
                      <td>
                        {inr(
                          Number(item.supplierProposedQty ?? item.quantity) *
                            Number(item.supplierProposedPrice ?? item.unitPrice),
                        )}
                      </td>
                    </>
                  )}
                  {canPropose && (
                    <td>
                      <input
                        className="sp-input"
                        type="number"
                        min="0"
                        step="any"
                        value={line?.proposedQty ?? ''}
                        onChange={(e) => updateLine(item.id, 'proposedQty', e.target.value)}
                      />
                    </td>
                  )}
                  {canPropose && (
                    <td>
                      <input
                        className="sp-input"
                        type="number"
                        min="0"
                        step="any"
                        value={line?.proposedPrice ?? ''}
                        onChange={(e) => updateLine(item.id, 'proposedPrice', e.target.value)}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showProposal && (
        <p className="sp-proposal-summary">
          Your proposed total: <strong>{inr(proposedTotal)}</strong> (before tax)
          {proposedDate && (
            <>
              {' '}
              · Delivery on <strong>{proposedDate}</strong>
              {dateMoved && requestedDate && <> (restaurant asked for {requestedDate})</>}
            </>
          )}
        </p>
      )}

      {!showProposal && requestedDate && (
        <p className="sp-proposal-summary">
          Restaurant needs delivery by <strong>{requestedDate}</strong>
        </p>
      )}

      {canPropose && (
        <div className="sp-form-row">
          <label htmlFor="deliveryDate">
            Proposed delivery date
            <input
              id="deliveryDate"
              className="sp-input"
              type="date"
              min={todayInput()}
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              style={{ maxWidth: 220 }}
            />
          </label>
        </div>
      )}

      <div className="sp-actions">
        {canPropose && (
          <button type="button" className="sp-btn sp-btn--primary" disabled={busy} onClick={handlePropose}>
            <i className="fa fa-paper-plane" aria-hidden="true" /> Send proposal
          </button>
        )}
        {canDispatch && (
          <button type="button" className="sp-btn sp-btn--primary" disabled={busy} onClick={handleDispatch}>
            <i className="fa fa-truck" aria-hidden="true" /> Mark dispatched
          </button>
        )}
        {po.status === 'SUPPLIER_PROPOSED' && (
          <p className="sp-muted">Waiting for restaurant to approve your proposal.</p>
        )}
      </div>
    </SupplierPageLayout>
  );
};

export default PODetail;
